<template>
  <div class="blog-list" >
    <transition
       :duration="70"
       mode="out-in"
       name="blog-admin"
       enter-active-class="animated fadeInLeft"
       leave-active-class="animated fadeOutRight" >
      <div v-if="post === null" key="list" >
        <div class="search-bar" >
          <el-form :inline="true" >
            <is-granted :condition="{attribute: 'search', subject: 'YWC\\MacaronsBlogBundle\\Entity\\Post'}" display="inline" >
              <el-form-item >
                <el-input
                   placeholder="Recherche"
                   v-model="title"
                   prefix-icon="el-icon-search" >
                </el-input>
              </el-form-item>
            </is-granted>
            <is-granted :condition="{attribute: 'create', subject: 'YWC\\MacaronsBlogBundle\\Entity\\Post'}" display="inline" >
              <el-button icon="el-icon-plus" @click="newPost" >Nouveau</el-button>
            </is-granted>
          </el-form>
        </div>
        <el-table
           v-loading="loading"
           :data="posts"
           :highlight-current-row="true"
           :stripe="true"
           @sort-change="handleSort" >
          <el-table-column width="120px" >
            <template slot-scope="{row}" >
              <el-tooltip v-if="isPublished(row)" placement="top" content="Publié" :open-delay="1000" >
                <i class="el-icon-fa-check-circle" ></i>
              </el-tooltip>
              <el-tooltip v-if="isExpired(row)" placement="top" content="Expiré" :open-delay="1000" >
                <i class="el-icon-fa-times-circle" ></i>
              </el-tooltip>
              <el-tooltip v-if="row.widget" placement="top" content="Widget" :open-delay="1000" >
                <i class="el-icon-fa-cubes" ></i>
              </el-tooltip>
              <el-tooltip v-if="row.event" placement="top" content="Évènement" :open-delay="1000" >
                <i class="el-icon-fa-calendar" ></i>
              </el-tooltip>
              <el-tooltip v-if="row.draft" placement="top" content="Brouillon" :open-delay="1000" >
                <i class="el-icon-fa-file-text-o" ></i>
              </el-tooltip>
            </template>
          </el-table-column>
          <el-table-column prop="title" label="Titre" :show-overflow-tooltip="true" >
          </el-table-column>
          <el-table-column label="Auteurs" :show-overflow-tooltip="true" >
            <template slot-scope="{row}" >
              {{ formatName(row) }}
            </template>
          </el-table-column>
          <el-table-column label="Date" sortable="custom" column-key="created" >
            <template slot-scope="{row}" >
              {{ formatDate(row.created) }}
            </template>
          </el-table-column>
          <el-table-column label="Actions" width="130px" >
            <el-button-group slot-scope="{row}" >
              <is-granted :condition="{attribute: 'edit', subject: 'YWC\\MacaronsBlogBundle\\Entity\\Post'}" display="inline" :wrap="false">
                <el-button
                  size="mini"
                  icon="el-icon-edit"
                  @click="setPost(row)"
                  title="Modifier" >
                </el-button>
              </is-granted>
              <is-granted :condition="{attribute: 'delete', subject: 'YWC\\MacaronsBlogBundle\\Entity\\Post'}" display="inline" :wrap="false">
                <el-button
                   size="mini"
                   icon="el-icon-delete"
                   type="danger"
                   title="Supprimer"
                   @click="handleDelete(row)"
                   >
                </el-button>
              </is-granted>
            </el-button-group>
          </el-table-column>
          <template v-if="count > limit" slot="append" >
            <el-pagination
               :small="true"
               layout="prev, pager, next"
               :page-size="limit"
               :total="count"
               :current-page="page"
               @current-change="handlePageChange" >
            </el-pagination>
          </template>
        </el-table>
      </div>
      <div v-else key="edit" class="edit-container" >
        <edit-post
           v-model="post"
           :quill="quill"
           @save="closeEdit(true)"
           @cancel="closeEdit(false)"
           @delete="closeEdit(true)" >
          <quill-toolbar slot="toolbar" :id="toolbarId" >
          </quill-toolbar>
        </edit-post>
      </div>
    </transition>
  </div>
</template>

<style scoped>
.container {
    margin: 30px;
}
.search-bar {
    text-align: right;
}
* >>> .quill-editor iframe {
    pointer-events: none;
}
.edit-container {
    margin-top: 80px;
}
.el-pagination {
    text-align: center;
    margin: 5px;
}

.is-granted .el-button--danger, .is-granted .el-button--default {
  border: none;
}
.blog-list {
    padding: 10px;
}
</style>

<script>
import Quill from 'quill'
import ImageResize from 'quill-image-resize-module'
import VideoResize from 'quill-video-resize-module'
import Link from '../quill/Link'
import {api, IsGranted} from 'macarons'
import moment from 'moment'
import EditPost from 'macarons-blog/EditPost.vue'
import QuillToolbar from './QuillToolbar.vue'
import ImageResizeFix from '../quill/ImageResizeFix'

let id = 0

Quill.debug(false)
Quill.register('modules/ImageResize', ImageResize)
Quill.register('modules/VideoResize', VideoResize)
Quill.register(Link)

export default {
  components: {
    EditPost: EditPost,
    QuillToolbar: QuillToolbar,
    IsGranted: IsGranted
  },
  props: {
    id: {
      type: [Number, String],
      required: null
    }
  },
  data () {
    return {
      toolbarId: null,
      searchTimeout: null,
      loading: false,
      title: null,
      posts: [],
      count: 0,
      limit: 10,
      offset: 0,
      page: 1,
      sort: {
        field: 'created',
        direction: 'ASC'
      },
      post: null
    }
  },
  mounted () {
    this.toolbarId = 'quill-toolbar-' + id++
    this.setPost()
    this.load()
  },
  watch: {
    title () {
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.load()
      }, 500)
    },
    id () {
      this.setPost()
    }
  },
  computed: {
    search () {
      return {
        title: this.title,
        limit: this.limit,
        offset: this.offset,
        sorts: [this.sort]
      }
    },
    quill () {
      return {
        modules: {
          toolbar: '#' + this.toolbarId,
          imageResize: {
            modules: [
              'Resize',
              'Toolbar',
              'DisplaySize',
              ImageResizeFix
            ]
          },
          videoResize: {}
        }
      }
    }
  },
  methods: {
    newPost () {
      this.setPost('new')
    },
    setPost (post) {
      post = typeof post === 'undefined' ? this.id : post
      if (post === 'new') {
        this.post = {}
        this.$router.push('/static/admin/new')
      } else if (typeof post === 'object') {
        this.post = post
        this.$router.push('/static/admin/' + (typeof post.id === 'undefined' ? 'new' : post.id))
      } else if (post) {
        this.post = {
          id: post
        }
        this.$router.push('/static/admin/' + post)
      } else {
        this.post = null
        this.$router.push('/static/admin')
      }
    },
    load () {
      this.loading = true
      api.post('blog/posts/search', this.search).then(({data}) => {
        this.posts.splice(0, this.posts.length, ...data.posts)
        this.count = data.count
        this.page = Math.floor(this.offset / this.limit) + 1
      }).finally(() => {
        this.loading = false
      })
    },
    handlePageChange (current) {
      this.offset = (current - 1) * this.limit
      this.load()
    },
    formatDate (date) {
      return moment(date).calendar()
    },
    formatName (row) {
      return row.authors.map(a => {
        if (a.lastname) {
          if (a.firstname) {
            return a.firstname.substr(0, 1) + '. ' + a.lastname
          }
          return a.lastname
        }
        return a.username
      }).join(', ')
    },
    isPublished (post) {
      return post.published && moment(post.published).isAfter()
    },
    isExpired (post) {
      return post.expiresAt && moment(post.expiresAt).isBefore(moment())
    },
    handleSort ({column, order}) {
      this.sort.field = column.columnKey
      this.sort.direction = order === 'descending' ? 'DESC' : 'ASC'
      this.load()
    },
    handleDelete (row) {
      this.$confirm('Le post sera définitivement supprimé. Poursuivre ?', 'danger', {
        confirmButtonText: 'Confirmer',
        cancelButtonText: 'Annuler',
        type: 'danger'
      }).then(() => {
        api.delete('blog/post/' + row.id).then(() => {
          this.$message({
            message: 'Le post "' + row.title + '" a été supprimé',
            type: 'success'
          })
          this.load()
        }).catch(() => {
          this.$message({
            message: 'Une erreur est survenue durant la suppression du post  "' + row.title + '"',
            type: 'error'
          })
        })
      }).catch(() => {})
    },
    closeEdit (reload) {
      this.post = null
      if (reload) {
        this.load()
      }
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/macarons/blog/BlogList.vue